import React, {
    useCallback, useEffect, useMemo, useRef, useState
} from 'react';
import {
    FiTrendingUp, FiDollarSign, FiCheckSquare, FiActivity,
    FiRefreshCw, FiEye, FiEyeOff, FiSearch, FiArrowRight,
    FiAlertTriangle, FiGrid, FiTruck, FiHeadphones,
    FiStar, FiClock, FiZap, FiChevronLeft, FiX
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../services/api';
import './DashboardAdmin.css';

/* =========================================================
 * Helpers
 * ========================================================= */
const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const NUM = new Intl.NumberFormat('pt-BR');

const fmtCur  = (v, vis = true) => vis ? BRL.format(+v || 0) : '••••••';
const fmtNum  = (v, vis = true) => vis ? NUM.format(+v || 0) : '••••';
const fmtPct  = (v, vis = true) => vis ? `${(+v || 0).toFixed(1)}%` : '••••';
const fmtCsat = (v, vis = true) => vis ? `${(+v || 0).toFixed(1)} ★` : '••••';

function useDebouncedValue(value, ms = 350) {
    const [dv, setDv] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDv(value), ms);
        return () => clearTimeout(t);
    }, [value, ms]);
    return dv;
}

function periodoLabel(p) {
    return { hoje: 'Hoje', semanal: 'Semanal', mensal: 'Mensal', anual: 'Anual' }[p] || 'Mensal';
}

/* =========================================================
 * Componente: InteractiveChart
 * ========================================================= */
function InteractiveChart({ data, title, subtitle, onBarClick, selectedMonth, loading }) {
    const [hovered, setHovered] = useState(null);
    const [tooltip, setTooltip] = useState({ x: 0, y: 0 });
    const svgRef = useRef(null);

    const CHART_H = 160;
    const BAR_W   = 14;
    const PAIR_W  = 38;

    const maxVal = useMemo(() => {
        if (!data) return 1;
        const allVals = [
            ...(data.faturamentoData || []),
            ...(data.lucroData       || []).map(Math.abs),
            ...(data.osData          || []).map(v => v * 1000),
        ];
        return Math.max(...allVals, 1);
    }, [data]);

    if (loading) return (
        <div className="da-chart-card" style={{ minHeight: 280 }}>
            <div className="da-chart-header">
                <div>
                    <div className="da-skeleton" style={{ width: 200, height: 20, marginBottom: 6 }} />
                    <div className="da-skeleton" style={{ width: 140, height: 14 }} />
                </div>
            </div>
            <div className="da-skeleton" style={{ height: 180, margin: '20px 0 0' }} />
        </div>
    );

    if (!data) return null;

    const labels  = data.labels          || [];
    const fatData = data.faturamentoData || [];
    const lucData = data.lucroData       || [];
    const osData  = data.osData          || [];
    const svgW    = labels.length * PAIR_W + 20;
    const barH    = (val) => Math.max(4, (Math.abs(val) / maxVal) * CHART_H);

    return (
        <div className="da-chart-card">
            <div className="da-chart-header">
                <div>
                    <h3 className="da-section-title">{title}</h3>
                    <p className="da-section-subtitle">{subtitle}</p>
                </div>
                <div className="da-chart-legend">
                    <span className="da-legend-item"><span className="da-legend-dot" style={{ background: 'var(--accent-blue)' }} />Faturamento</span>
                    <span className="da-legend-item"><span className="da-legend-dot" style={{ background: 'var(--accent-green)' }} />Lucro</span>
                    <span className="da-legend-item"><span className="da-legend-dot" style={{ background: 'var(--accent-amber)' }} />OS</span>
                </div>
            </div>

            {hovered !== null && (
                <div className="da-chart-tooltip" style={{ left: tooltip.x, top: tooltip.y }}>
                    <strong>{labels[hovered]}</strong>
                    <span>Faturamento: {fmtCur(fatData[hovered])}</span>
                    <span className={lucData[hovered] < 0 ? 'neg' : 'pos'}>Lucro: {fmtCur(lucData[hovered])}</span>
                    <span>OS: {fmtNum(osData[hovered])}</span>
                </div>
            )}

            <div className="da-svg-wrap" ref={svgRef}>
                <svg
                    viewBox={`0 0 ${svgW} ${CHART_H + 36}`}
                    width={svgW}
                    height={CHART_H + 36}
                    style={{ display: 'block', minWidth: '100%' }}
                >
                    {labels.map((label, i) => {
                        const isSelected = selectedMonth === i;
                        const isHov      = hovered === i;
                        const x          = i * PAIR_W + 10;
                        const fatH       = barH(fatData[i]);
                        const lucH       = barH(lucData[i]);
                        const lucNeg     = (lucData[i] || 0) < 0;
                        const fade       = selectedMonth !== null && !isSelected;
                        const opacity    = fade ? 0.28 : 1;

                        return (
                            <g
                                key={label}
                                style={{ cursor: 'pointer', transition: 'opacity .25s' }}
                                opacity={opacity}
                                onClick={() => onBarClick(isSelected ? null : i)}
                                onMouseEnter={(e) => {
                                    setHovered(i);
                                    const rect = svgRef.current?.getBoundingClientRect();
                                    setTooltip({ x: e.clientX - (rect?.left || 0) + 8, y: 0 });
                                }}
                                onMouseLeave={() => setHovered(null)}
                            >
                                <rect
                                    x={x} y={CHART_H - fatH} width={BAR_W} height={fatH} rx="3"
                                    fill={isSelected || isHov ? 'var(--accent-blue)' : 'var(--accent-blue-muted)'}
                                    style={{ transition: 'fill .2s, height .4s, y .4s' }}
                                />
                                <rect
                                    x={x + BAR_W + 2}
                                    y={lucNeg ? CHART_H : CHART_H - lucH}
                                    width={BAR_W} height={lucH} rx="3"
                                    fill={lucNeg ? 'var(--accent-red-muted)' : (isSelected || isHov ? 'var(--accent-green)' : 'var(--accent-green-muted)')}
                                    style={{ transition: 'fill .2s, height .4s, y .4s' }}
                                />
                                <text
                                    x={x + BAR_W} y={CHART_H + 18}
                                    textAnchor="middle" fontSize="9"
                                    fill={isSelected ? 'var(--text-primary)' : 'var(--text-muted)'}
                                    fontWeight={isSelected ? '700' : '500'}
                                >
                                    {label}
                                </text>
                                {isSelected && (
                                    <circle cx={x + BAR_W} cy={CHART_H + 28} r="3" fill="var(--accent-blue)" />
                                )}
                            </g>
                        );
                    })}
                    <line x1="0" y1={CHART_H} x2={svgW} y2={CHART_H} stroke="var(--border)" strokeWidth="1" />
                </svg>
            </div>

            {selectedMonth !== null && (
                <div className="da-month-detail">
                    <strong>{labels[selectedMonth]}</strong>
                    <div className="da-month-pills">
                        <span className="pill blue">{fmtCur(fatData[selectedMonth])}</span>
                        <span className={`pill ${(lucData[selectedMonth] || 0) < 0 ? 'red' : 'green'}`}>{fmtCur(lucData[selectedMonth])}</span>
                        <span className="pill amber">{fmtNum(osData[selectedMonth])} OS</span>
                    </div>
                    <button className="da-month-close" onClick={() => onBarClick(null)}><FiX size={12} /></button>
                </div>
            )}
        </div>
    );
}

/* =========================================================
 * Componente: KpiCard — com suporte a drag
 * ========================================================= */
function KpiCard({ kpi, isLoading, isVisible, isDragging, onDragStart, onDragOver, onDrop, onDragEnd }) {
    return (
        <div
            className={`da-kpi-card da-kpi-card--${kpi.variant} ${isDragging ? 'dragging' : ''}`}
            draggable
            onDragStart={onDragStart}
            onDragOver={(e) => { e.preventDefault(); onDragOver(); }}
            onDrop={onDrop}
            onDragEnd={onDragEnd}
        >
            <div className="da-kpi-drag-handle" title="Arrastar para reordenar">⠿</div>
            <div className="da-kpi-icon">{kpi.icon}</div>
            <div className="da-kpi-info">
                {isLoading ? (
                    <>
                        <div className="da-skeleton" style={{ width: '70%', height: 26, marginBottom: 6 }} />
                        <div className="da-skeleton" style={{ width: '85%', height: 14 }} />
                    </>
                ) : (
                    <>
                        <span className="da-kpi-value">{kpi.format(isVisible)}</span>
                        <span className="da-kpi-title">{kpi.label}</span>
                    </>
                )}
            </div>
        </div>
    );
}

/* =========================================================
 * Componente: TenantPreview
 * ========================================================= */
function TenantPreview({ tenant, resumo, loading, onOpen, onClose, visible }) {
    if (!tenant) return (
        <div className="da-section-card da-preview-card">
            <h2 className="da-section-title">Prévia do Tenant</h2>
            <div className="da-empty-state">Selecione um tenant para ver os detalhes aqui.</div>
        </div>
    );

    return (
        <div className="da-section-card da-preview-card">
            <div className="da-preview-top">
                <div>
                    <div className="da-preview-name">{tenant.tenantNome}</div>
                    <div className="da-preview-sub">Tenant #{tenant.id}</div>
                </div>
                <div className="da-preview-actions">
                    <button className="da-icon-btn" onClick={onClose} title="Fechar prévia"><FiX /></button>
                    <button className="da-btn-primary" onClick={() => onOpen(tenant)}>
                        Ver dashboard <FiArrowRight size={13} />
                    </button>
                </div>
            </div>

            {loading ? (
                <div style={{ display: 'grid', gap: 10, marginTop: 16 }}>
                    {[1, 2, 3, 4, 5].map(i => (
                        <div key={i} className="da-skeleton" style={{ height: 54, borderRadius: 12 }} />
                    ))}
                </div>
            ) : resumo ? (
                <>
                    <div className="da-preview-section-label"><FiDollarSign size={12} /> Financeiro</div>
                    <div className="da-preview-metrics">
                        <div className="da-pm-card"><span>Faturamento</span><strong>{fmtCur(resumo.faturamento, visible)}</strong></div>
                        <div className="da-pm-card"><span>Despesas</span><strong className="neg">{fmtCur(resumo.despesas, visible)}</strong></div>
                        <div className={`da-pm-card ${resumo.lucro < 0 ? 'neg' : 'pos'}`}><span>Lucro</span><strong>{fmtCur(resumo.lucro, visible)}</strong></div>
                        <div className="da-pm-card"><span>Margem</span><strong>{fmtPct(resumo.margem, visible)}</strong></div>
                    </div>

                    {resumo.os && (
                        <>
                            <div className="da-preview-section-label"><FiTruck size={12} /> Operacional</div>
                            <div className="da-preview-metrics">
                                <div className="da-pm-card"><span>Total OS</span><strong>{fmtNum(resumo.os.total, visible)}</strong></div>
                                <div className="da-pm-card pos"><span>Concluídas</span><strong>{fmtNum(resumo.os.concluidas, visible)}</strong></div>
                                <div className="da-pm-card amber"><span>Em andamento</span><strong>{fmtNum(resumo.os.em_andamento, visible)}</strong></div>
                                <div className="da-pm-card"><span>Taxa conclusão</span><strong>{fmtPct(resumo.os.taxa_conclusao, visible)}</strong></div>
                                <div className="da-pm-card"><span>Duração média</span><strong>{visible ? `${resumo.os.media_dur_min}min` : '••••'}</strong></div>
                            </div>
                        </>
                    )}

                    {resumo.suporte && (
                        <>
                            <div className="da-preview-section-label"><FiHeadphones size={12} /> Suporte</div>
                            <div className="da-preview-metrics">
                                <div className="da-pm-card"><span>Chamados</span><strong>{fmtNum(resumo.suporte.total, visible)}</strong></div>
                                <div className={`da-pm-card ${resumo.suporte.chamados_abertos > 0 ? 'amber' : 'pos'}`}><span>Abertos</span><strong>{fmtNum(resumo.suporte.abertos, visible)}</strong></div>
                                <div className={`da-pm-card ${resumo.suporte.urgentes > 0 ? 'neg' : ''}`}><span>Urgentes</span><strong>{fmtNum(resumo.suporte.urgentes, visible)}</strong></div>
                                <div className="da-pm-card"><span>Taxa resolução</span><strong>{fmtPct(resumo.suporte.taxa_resolucao, visible)}</strong></div>
                                <div className={`da-pm-card ${resumo.suporte.media_csat >= 4 ? 'pos' : resumo.suporte.media_csat >= 3 ? 'amber' : 'neg'}`}><span>CSAT médio</span><strong>{fmtCsat(resumo.suporte.media_csat, visible)}</strong></div>
                            </div>
                        </>
                    )}
                </>
            ) : (
                <div className="da-empty-state" style={{ marginTop: 20 }}>Não foi possível carregar o resumo.</div>
            )}
        </div>
    );
}

/* =========================================================
 * Componente: AlertCard
 * ========================================================= */
function AlertCard({ alert }) {
    const c = {
        alta:  { bg: 'rgba(239,68,68,.09)',   border: 'rgba(239,68,68,.3)',   color: '#dc2626' },
        media: { bg: 'rgba(245,158,11,.09)',  border: 'rgba(245,158,11,.3)', color: '#d97706' },
    }[alert.severidade] || { bg: 'rgba(59,130,246,.09)', border: 'rgba(59,130,246,.3)', color: '#2563eb' };

    return (
        <div className="da-alert-card" style={{ background: c.bg, borderColor: c.border }}>
            <FiAlertTriangle style={{ color: c.color, flexShrink: 0, marginTop: 1 }} />
            <div>
                <div className="da-alert-title">{alert.tenantNome}</div>
                <div className="da-alert-text">{alert.mensagem}</div>
            </div>
        </div>
    );
}

/* =========================================================
 * Main: DashboardAdmin
 * ========================================================= */
export default function DashboardAdmin({ user }) {
    /* ── state ── */
    const [periodo, setPeriodo]       = useState('mensal');
    const [loading, setLoading]       = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError]           = useState('');
    const [visible, setVisible]       = useState(true);
    const [search, setSearch]         = useState('');
    const dSearch = useDebouncedValue(search, 350);
    const [activeTab, setActiveTab]   = useState('financeiro');

    /* ── data ── */
    const [overview, setOverview]   = useState(null);
    const [evolution, setEvolution] = useState(null);
    const [tenants, setTenants]     = useState([]);
    const [opData, setOpData]       = useState([]);
    const [supData, setSupData]     = useState([]);

    /* ── chart state ── */
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [chartTenant, setChartTenant]     = useState(null);
    const [chartData, setChartData]         = useState(null);
    const [chartLoading, setChartLoading]   = useState(false);

    /* ── tenant preview ── */
    const [selectedTenant, setSelectedTenant]               = useState(null);
    const [tenantResumo, setTenantResumo]                   = useState(null);
    const [tenantResumoLoading, setTenantResumoLoading]     = useState(false);

    /* ── draggable KPI order ── */
    const [kpiOrder, setKpiOrder] = useState([0, 1, 2, 3, 4, 5, 6]);
    const dragSrc  = useRef(null);
    const abortRef = useRef(null);

    const firstName = useMemo(() => (user?.nome || user?.name || 'Admin').split(' ')[0], [user]);

    /* ── KPI definitions ── */
    const totais  = overview?.totais || {};
    const allKpis = useMemo(() => [
        { id: 0, label: `Faturamento ${periodoLabel(periodo)}`, icon: <FiTrendingUp />,  variant: 'faturamento', format: (v) => fmtCur(totais.faturamento        || 0, v) },
        { id: 1, label: `Lucro ${periodoLabel(periodo)}`,       icon: <FiDollarSign />,  variant: 'lucro',       format: (v) => fmtCur(totais.lucro              || 0, v) },
        { id: 2, label: `Despesas ${periodoLabel(periodo)}`,    icon: <FiActivity />,    variant: 'despesas',    format: (v) => fmtCur(totais.despesas           || 0, v) },
        { id: 3, label: 'Serviços concluídos',                  icon: <FiCheckSquare />, variant: 'servicos',    format: (v) => fmtNum(totais.servicosConcluidos || 0, v) },
        { id: 4, label: 'OS em andamento agora',                icon: <FiTruck />,       variant: 'andamento',   format: (v) => fmtNum(totais.osEmAndamento      || 0, v) },
        { id: 5, label: 'Tickets abertos agora',                icon: <FiHeadphones />,  variant: 'tickets',     format: (v) => fmtNum(totais.ticketsAbertos     || 0, v) },
        { id: 6, label: 'CSAT médio global',                    icon: <FiStar />,        variant: 'csat',        format: (v) => fmtCsat(totais.mediaCsat         || 0, v) },
    ], [totais, periodo]);

    /* ── fetch principal ── */
    const fetchAll = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        setRefreshing(true);
        setError('');
        abortRef.current?.abort();
        const ctrl = new AbortController();
        abortRef.current = ctrl;

        try {
            const [ov, ev, tn, op, sp] = await Promise.all([
                api.get(`/api/dashboard/admin/overview?periodo=${periodo}&limit=8`,                                          { signal: ctrl.signal }),
                api.get(`/api/dashboard/admin/evolucao-mensal`,                                                              { signal: ctrl.signal }),
                api.get(`/api/dashboard/admin/tenants?periodo=${periodo}&limit=200&search=${encodeURIComponent(dSearch)}`,   { signal: ctrl.signal }),
                api.get(`/api/dashboard/admin/operacional?periodo=${periodo}&limit=200`,                                     { signal: ctrl.signal }),
                api.get(`/api/dashboard/admin/suporte?periodo=${periodo}&limit=200`,                                         { signal: ctrl.signal }),
            ]);
            if (ctrl.signal.aborted) return;
            setOverview(ov.data);
            setEvolution(ev.data);
            setChartData(ev.data);
            setTenants(Array.isArray(tn.data) ? tn.data : []);
            setOpData(Array.isArray(op.data)  ? op.data  : []);
            setSupData(Array.isArray(sp.data) ? sp.data  : []);
        } catch (err) {
            if (!ctrl.signal.aborted) {
                setError(err?.response?.data?.error || 'Falha ao carregar o painel gerencial.');
            }
        } finally {
            if (!ctrl?.signal?.aborted) { setLoading(false); setRefreshing(false); }
        }
    }, [periodo, dSearch]);

    useEffect(() => {
        fetchAll(false);
        const onFocus = () => fetchAll(true);
        window.addEventListener('focus', onFocus);
        return () => { window.removeEventListener('focus', onFocus); abortRef.current?.abort(); };
    }, [fetchAll]);

    /* ── Clique em tenant → prévia + gráfico ── */
    const handleTenantClick = useCallback(async (tenant) => {
        setSelectedTenant(tenant);
        setSelectedMonth(null);

        setTenantResumoLoading(true);
        try {
            const { data } = await api.get(`/api/dashboard/admin/tenant-resumo/${tenant.id}?periodo=${periodo}`);
            setTenantResumo(data);
        } catch {
            setTenantResumo(null);
        } finally {
            setTenantResumoLoading(false);
        }

        if (chartTenant?.id === tenant.id) {
            setChartTenant(null);
            setChartData(evolution);
            return;
        }

        setChartLoading(true);
        setChartTenant(tenant);
        try {
            const { data } = await api.get(`/api/dashboard/admin/evolucao-mensal?tenantId=${tenant.id}`);
            setChartData(data);
        } catch {
            setChartData(evolution);
        } finally {
            setChartLoading(false);
        }
    }, [chartTenant, evolution, periodo]);

    const resetChart = useCallback(() => {
        setChartTenant(null);
        setChartData(evolution);
        setSelectedMonth(null);
        setSelectedTenant(null);
        setTenantResumo(null);
    }, [evolution]);

    /* ── Enter tenant view — Impersonation Token ────────────────────────
     * Solicita ao backend um JWT temporário (2h) com cliente_id do tenant-alvo.
     * O authMiddleware usará esse cliente_id para fixar req.tenantId,
     * garantindo isolamento total de dados sem query params manipuláveis.
     * ─────────────────────────────────────────────────────────────────── */
    const enterTenantView = useCallback(async (tenant) => {
        if (!tenant?.id) return;

        try {
            // [FASE 3] Entra no tenant via switch-tenant: o backend reemite o
            // cookie de sessão com active_tenant_id (mesma sessão, perfil intacto).
            // NADA é guardado no navegador — sem token, sem adminOriginalToken.
            await api.post('/api/auth/switch-tenant', { cliente_id: tenant.id });
            window.location.assign('/dashboard');
        } catch (err) {
            toast.error(err?.response?.data?.error || 'Não foi possível acessar este tenant.');
        }
    }, []);

    /* ── Drag KPI ── */
    const handleKpiDragStart = (idx) => { dragSrc.current = idx; };
    const handleKpiDragOver  = (idx) => {
        if (dragSrc.current === null || dragSrc.current === idx) return;
        setKpiOrder(prev => {
            const next = [...prev];
            const from = next.indexOf(prev[dragSrc.current]);
            const to   = next.indexOf(prev[idx]);
            [next[from], next[to]] = [next[to], next[from]];
            dragSrc.current = idx;
            return next;
        });
    };
    const handleKpiDragEnd = () => { dragSrc.current = null; };

    /* ── Computed ── */
    const orderedKpis   = useMemo(() => kpiOrder.map(i => allKpis[i]), [kpiOrder, allKpis]);
    const alertas       = overview?.alertas || [];
    const ranking       = overview?.ranking  || [];
    const sortedTenants = useMemo(() => [...tenants].sort((a, b) => b.faturamento   - a.faturamento),   [tenants]);
    const sortedOp      = useMemo(() => [...opData].sort((a, b)  => b.total_os      - a.total_os),      [opData]);
    const sortedSup     = useMemo(() => [...supData].sort((a, b) => b.total_chamados - a.total_chamados), [supData]);

    const chartTitle    = chartTenant ? `Evolução — ${chartTenant.tenantNome}` : 'Evolução consolidada';
    const chartSubtitle = chartTenant
        ? `Dados individuais do tenant · ${periodoLabel(periodo)}`
        : 'Faturamento, lucro e OS por mês (ano atual)';

    /* =========================================================
     * RENDER
     * ========================================================= */
    return (
        <div className="da-container">

            {/* ── Header ── */}
            <header className="da-header">
                <div>
                    <h1 className="da-page-title">Painel Gerencial</h1>
                    <p className="da-page-subtitle">
                        Olá, <strong>{firstName}</strong> — visão multi-tenant consolidada · OS + Suporte integrados
                    </p>
                </div>
                <div className="da-header-actions">
                    <div className="da-period-group">
                        {['hoje', 'semanal', 'mensal', 'anual'].map(p => (
                            <button
                                key={p} type="button"
                                className={`da-period-btn${periodo === p ? ' da-period-btn--active' : ''}`}
                                onClick={() => setPeriodo(p)}
                            >
                                {periodoLabel(p)}
                            </button>
                        ))}
                    </div>
                    <div className="da-actions-group">
                        <button type="button" className="da-icon-btn" onClick={() => fetchAll(false)} title="Atualizar">
                            <FiRefreshCw className={refreshing ? 'spin-soft' : ''} />
                        </button>
                        <button type="button" className="da-icon-btn" onClick={() => setVisible(v => !v)} title="Ocultar valores">
                            {visible ? <FiEye /> : <FiEyeOff />}
                        </button>
                    </div>
                </div>
            </header>

            {error && <div className="da-error-box" role="alert">{error}</div>}

            {/* ── KPIs draggáveis ── */}
            <section className="da-kpi-section">
                <div className="da-kpi-hint"><FiZap size={11} /> Arraste os cards para reorganizar</div>
                <div className="da-kpi-grid">
                    {orderedKpis.map((kpi, displayIdx) => (
                        <KpiCard
                            key={kpi.id}
                            kpi={kpi}
                            isLoading={loading}
                            isVisible={visible}
                            isDragging={dragSrc.current === displayIdx}
                            onDragStart={() => handleKpiDragStart(displayIdx)}
                            onDragOver={() => handleKpiDragOver(displayIdx)}
                            onDrop={() => {}}
                            onDragEnd={handleKpiDragEnd}
                        />
                    ))}
                </div>
            </section>

            {/* ── Alertas ── */}
            {(loading || alertas.length > 0) && (
                <section className="da-section-card">
                    <div className="da-section-header">
                        <div>
                            <h2 className="da-section-title">Alertas gerenciais</h2>
                            <p className="da-section-subtitle">Sinais automáticos para tomada de decisão</p>
                        </div>
                    </div>
                    {loading ? (
                        <div className="da-alert-grid">
                            {[1, 2, 3].map(i => (
                                <div key={i} className="da-skeleton" style={{ height: 72, borderRadius: 14 }} />
                            ))}
                        </div>
                    ) : (
                        <div className="da-alert-grid">
                            {alertas.map((a, i) => <AlertCard key={i} alert={a} />)}
                        </div>
                    )}
                </section>
            )}

            {/* ── Gráfico + Ranking ── */}
            <section className="da-two-col">
                <div style={{ position: 'relative' }}>
                    {chartTenant && (
                        <div className="da-chart-tenant-banner">
                            <FiChevronLeft size={13} />
                            Exibindo dados de <strong>{chartTenant.tenantNome}</strong>
                            <button className="da-chart-reset-btn" onClick={resetChart}>Voltar ao global</button>
                        </div>
                    )}
                    <InteractiveChart
                        data={chartData || evolution}
                        title={chartTitle}
                        subtitle={chartSubtitle}
                        onBarClick={setSelectedMonth}
                        selectedMonth={selectedMonth}
                        loading={chartLoading || (loading && !chartData)}
                    />
                </div>

                <div className="da-section-card">
                    <div className="da-section-header">
                        <div>
                            <h2 className="da-section-title">Top tenants</h2>
                            <p className="da-section-subtitle">Clique para filtrar o gráfico</p>
                        </div>
                    </div>
                    {loading ? (
                        <div style={{ display: 'grid', gap: 10 }}>
                            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                                <div key={i} className="da-skeleton" style={{ height: 44, borderRadius: 12 }} />
                            ))}
                        </div>
                    ) : ranking.length === 0 ? (
                        <div className="da-empty-state">Nenhum dado no período.</div>
                    ) : (
                        <div style={{ display: 'grid', gap: 8 }}>
                            {ranking.map((item, idx) => {
                                const isActive = chartTenant?.id === item.id;
                                return (
                                    <button
                                        key={item.id} type="button"
                                        className={`da-top-tenant-btn ${isActive ? 'active' : ''}`}
                                        onClick={() => handleTenantClick(item)}
                                        title="Clique para ver evolução deste tenant no gráfico"
                                    >
                                        <div className="da-top-tenant-left">
                                            <div className="da-top-tenant-badge">{idx + 1}</div>
                                            <div>
                                                <div className="da-top-tenant-name">{item.tenantNome}</div>
                                                <div className="da-top-tenant-meta">{fmtCur(item.faturamento, visible)}</div>
                                            </div>
                                        </div>
                                        <div className={`da-top-tenant-right ${item.lucro < 0 ? 'da-top-tenant-right--neg' : 'da-top-tenant-right--pos'}`}>
                                            {fmtCur(item.lucro, visible)}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>

            {/* ── Tabs: Financeiro | Operacional | Suporte ── */}
            <section className="da-main-section">
                <div className="da-tab-bar">
                    {[
                        { id: 'financeiro',  label: 'Financeiro',  icon: <FiDollarSign size={14} /> },
                        { id: 'operacional', label: 'Operacional', icon: <FiTruck size={14} />      },
                        { id: 'suporte',     label: 'Suporte',     icon: <FiHeadphones size={14} /> },
                    ].map(tab => (
                        <button
                            key={tab.id}
                            className={`da-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.icon} {tab.label}
                        </button>
                    ))}
                    <div className="da-search-wrap">
                        <FiSearch size={14} />
                        <input
                            type="text" value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Buscar empresa..."
                            className="da-search-input"
                        />
                    </div>
                </div>

                <div className="da-main-grid">
                    <div className="da-section-card da-table-card">
                        <div className="da-table-wrap">

                            {/* ── Tab: Financeiro ── */}
                            {activeTab === 'financeiro' && (
                                <table className="da-table">
                                    <thead><tr>
                                        <th className="da-th">Empresa</th>
                                        <th className="da-th">Faturamento</th>
                                        <th className="da-th">Despesas</th>
                                        <th className="da-th">Lucro</th>
                                        <th className="da-th">Serviços</th>
                                        <th className="da-th">Margem</th>
                                        <th className="da-th">Ações</th>
                                    </tr></thead>
                                    <tbody>
                                        {loading ? [1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                                            <tr key={i}><td colSpan={7} className="da-td">
                                                <div className="da-skeleton" style={{ height: 38, borderRadius: 8 }} />
                                            </td></tr>
                                        )) : sortedTenants.length === 0 ? (
                                            <tr><td colSpan={7} className="da-td--empty">Nenhum tenant encontrado.</td></tr>
                                        ) : sortedTenants.map(t => (
                                            <tr
                                                key={t.id}
                                                className={selectedTenant?.id === t.id ? 'da-tr--selected' : ''}
                                                onClick={() => handleTenantClick(t)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <td className="da-td--strong">{t.tenantNome}</td>
                                                <td className="da-td">{fmtCur(t.faturamento, visible)}</td>
                                                <td className="da-td neg">{fmtCur(t.despesas, visible)}</td>
                                                <td className={`da-td ${t.lucro < 0 ? 'neg' : 'pos'}`}>{fmtCur(t.lucro, visible)}</td>
                                                <td className="da-td">{fmtNum(t.servicosConcluidos, visible)}</td>
                                                <td className="da-td">{fmtPct(t.margem, visible)}</td>
                                                <td className="da-td" onClick={e => e.stopPropagation()}>
                                                    <div className="da-row-actions">
                                                        <button className="da-btn-secondary" onClick={() => handleTenantClick(t)}>Prévia</button>
                                                        <button className="da-btn-primary"   onClick={() => enterTenantView(t)}>Entrar <FiArrowRight size={12} /></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}

                            {/* ── Tab: Operacional ── */}
                            {activeTab === 'operacional' && (
                                <table className="da-table">
                                    <thead><tr>
                                        <th className="da-th">Empresa</th>
                                        <th className="da-th">Total OS</th>
                                        <th className="da-th">Em andamento</th>
                                        <th className="da-th">Concluídas</th>
                                        <th className="da-th">Taxa</th>
                                        <th className="da-th">Duração média</th>
                                        <th className="da-th">Seguradora</th>
                                        <th className="da-th">App</th>
                                    </tr></thead>
                                    <tbody>
                                        {loading ? [1, 2, 3, 4, 5].map(i => (
                                            <tr key={i}><td colSpan={8} className="da-td">
                                                <div className="da-skeleton" style={{ height: 38, borderRadius: 8 }} />
                                            </td></tr>
                                        )) : sortedOp.length === 0 ? (
                                            <tr><td colSpan={8} className="da-td--empty">Nenhum dado operacional no período.</td></tr>
                                        ) : sortedOp.map(r => (
                                            <tr
                                                key={r.id}
                                                className={selectedTenant?.id === r.id ? 'da-tr--selected' : ''}
                                                onClick={() => handleTenantClick(r)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <td className="da-td--strong">{r.tenantNome}</td>
                                                <td className="da-td">{fmtNum(r.total_os, visible)}</td>
                                                <td className="da-td amber">{fmtNum(r.os_em_andamento, visible)}</td>
                                                <td className="da-td pos">{fmtNum(r.os_concluidas, visible)}</td>
                                                <td className="da-td">
                                                    <div className="da-progress-mini">
                                                        <div className="da-progress-fill" style={{ width: `${Math.min(r.taxa_conclusao, 100)}%` }} />
                                                    </div>
                                                    <span style={{ fontSize: '0.78rem' }}>{fmtPct(r.taxa_conclusao, visible)}</span>
                                                </td>
                                                <td className="da-td">
                                                    <FiClock size={11} style={{ marginRight: 4, opacity: .5 }} />
                                                    {visible ? `${r.media_duracao_min}min` : '••••'}
                                                </td>
                                                <td className="da-td">{fmtNum(r.os_seguradora, visible)}</td>
                                                <td className="da-td">{fmtNum(r.os_app, visible)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}

                            {/* ── Tab: Suporte ── */}
                            {activeTab === 'suporte' && (
                                <table className="da-table">
                                    <thead><tr>
                                        <th className="da-th">Empresa</th>
                                        <th className="da-th">Total</th>
                                        <th className="da-th">Abertos</th>
                                        <th className="da-th">Urgentes</th>
                                        <th className="da-th">Resolvidos</th>
                                        <th className="da-th">Taxa res.</th>
                                        <th className="da-th">CSAT</th>
                                        <th className="da-th">T.M. Resolução</th>
                                    </tr></thead>
                                    <tbody>
                                        {loading ? [1, 2, 3, 4, 5].map(i => (
                                            <tr key={i}><td colSpan={8} className="da-td">
                                                <div className="da-skeleton" style={{ height: 38, borderRadius: 8 }} />
                                            </td></tr>
                                        )) : sortedSup.length === 0 ? (
                                            <tr><td colSpan={8} className="da-td--empty">Nenhum dado de suporte no período.</td></tr>
                                        ) : sortedSup.map(r => (
                                            <tr
                                                key={r.id}
                                                className={selectedTenant?.id === r.id ? 'da-tr--selected' : ''}
                                                onClick={() => handleTenantClick(r)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <td className="da-td--strong">{r.tenantNome}</td>
                                                <td className="da-td">{fmtNum(r.total_chamados, visible)}</td>
                                                <td className={`da-td ${r.chamados_abertos  > 0 ? 'amber' : ''}`}>{fmtNum(r.chamados_abertos, visible)}</td>
                                                <td className={`da-td ${r.chamados_urgentes > 0 ? 'neg'   : ''}`}>{fmtNum(r.chamados_urgentes, visible)}</td>
                                                <td className="da-td pos">{fmtNum(r.chamados_resolvidos, visible)}</td>
                                                <td className="da-td">
                                                    <div className="da-progress-mini">
                                                        <div className="da-progress-fill green" style={{ width: `${Math.min(r.taxa_resolucao, 100)}%` }} />
                                                    </div>
                                                    <span style={{ fontSize: '0.78rem' }}>{fmtPct(r.taxa_resolucao, visible)}</span>
                                                </td>
                                                <td className={`da-td ${r.media_csat >= 4 ? 'pos' : r.media_csat >= 3 ? 'amber' : r.media_csat > 0 ? 'neg' : ''}`}>
                                                    {fmtCsat(r.media_csat, visible)}
                                                </td>
                                                <td className="da-td">
                                                    <FiClock size={11} style={{ marginRight: 4, opacity: .5 }} />
                                                    {visible ? `${r.tempo_medio_resolucao_h}h` : '••••'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>

                    <TenantPreview
                        tenant={selectedTenant}
                        resumo={tenantResumo}
                        loading={tenantResumoLoading}
                        onOpen={enterTenantView}
                        onClose={resetChart}
                        visible={visible}
                    />
                </div>
            </section>

        </div>
    );
}